import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "utils";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

/**
 * The close button in a dialogue's or a drawer's corner (Jira NAV-228): named
 * in the reader's language and shown as a tooltip like every other icon-only
 * button.
 *
 * **On hover only.** A dialogue can open with focus on this button, and a
 * tooltip opened by that focus is a layer of its own above the dialogue, so it
 * would take the first Escape and the dialogue would need a second. Preventing
 * the focus event's default is what makes the trigger's composed handler skip.
 */
export function DialogCloseButton({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const label = t("common:close");
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogPrimitive.Close
            onFocus={(event) => event.preventDefault()}
            className={cn(
              "absolute right-4 top-4 rounded-sm p-[0.5rem] opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
              className,
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{label}</span>
          </DialogPrimitive.Close>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{label}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}
